import { storage } from '../api'

/** How a viewer likes subtitles; kept per device. */
export interface SubtitleStyle {
  /** Text size relative to the default. */
  size: number
  font: SubtitleFont
  color: string
  edge: SubtitleEdge
  /** Opacity of the box behind the text, 0 for none. */
  background: number
  /** Where text subtitles sit: the center of their bottom edge, in percent
   * of the picture from its left and bottom edges. Viewers drag them there. */
  x: number
  y: number
  /** Seconds to show subtitles later (positive) or earlier (negative). */
  delay: number
  /** ASS tracks: the author's styling, or plain text styled as above. */
  ass: 'original' | 'plain'
}

export type SubtitleFont = 'sans' | 'serif' | 'mono' | 'rounded'
export type SubtitleEdge = 'none' | 'outline' | 'shadow'

export const DEFAULT_STYLE: SubtitleStyle = {
  size: 1,
  font: 'sans',
  color: '#ffffff',
  edge: 'shadow',
  background: 0.6,
  x: 50,
  y: 9,
  delay: 0,
  ass: 'original',
}

export const COLORS = ['#ffffff', '#ffe066', '#7fd1c7', '#9fd3ff', '#ffb3c7']
export const SIZE_RANGE = { min: 0.5, max: 2.5, step: 0.1 }
export const DELAY_RANGE = { min: -120, max: 120, step: 0.1 }
// Subtitles stay this far (percent) from the picture edges.
export const POSITION_RANGE = { x: { min: 5, max: 95 }, y: { min: 0, max: 90 } }

const KEY = 'chillio.subtitleStyle'

export function loadStyle(): SubtitleStyle {
  try {
    const saved = JSON.parse(storage.get(KEY) ?? '{}') as Partial<SubtitleStyle> & { lift?: unknown }
    delete saved.lift // replaced by x and y
    return { ...DEFAULT_STYLE, ...saved }
  } catch {
    return DEFAULT_STYLE
  }
}

export function saveStyle(s: SubtitleStyle) {
  storage.set(KEY, JSON.stringify(s))
}

export const clamp = (v: number, r: { min: number; max: number }) => Math.min(r.max, Math.max(r.min, Math.round(v * 10) / 10))

export const FONT_FAMILIES: Record<SubtitleFont, string> = {
  sans: "'Golos Text', system-ui, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'JetBrains Mono', ui-monospace, Consolas, monospace",
  rounded: "'Nunito', 'Arial Rounded MT Bold', system-ui, sans-serif",
}

const EDGES: Record<SubtitleEdge, string> = {
  none: 'none',
  outline: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 3px #000',
  shadow: '0 2px 4px rgba(0, 0, 0, 0.9), 0 0 2px rgba(0, 0, 0, 0.8)',
}

/** CSS custom properties that style text cues (see .player__cues). */
export function styleVars(s: SubtitleStyle): Record<string, string> {
  return {
    '--sub-scale': String(s.size),
    '--sub-font': FONT_FAMILIES[s.font],
    '--sub-color': s.color,
    '--sub-edge': EDGES[s.edge],
    '--sub-bg': `rgba(5, 6, 12, ${s.background})`,
    '--sub-x': `${s.x}%`,
    '--sub-y': `${s.y}%`,
  }
}
